import React from 'react';
import { useTranslation } from 'react-i18next';
import { useVersionChecker } from './VersionService';
import styles from './VersionUpdateNotification.module.css';

interface VersionUpdateNotificationProps {
    enabled?: boolean;
    checkInterval?: number;
    position?: 'top' | 'bottom';
    className?: string;
    onDismiss?: () => void;
    onRefresh?: () => void;
}

/**
 * Component that shows a notification banner when a new version of the app is available.
 * It depends on the `useVersionChecker` hook to check for updates, which polls a version file
 * generated at build time.
 */
export const VersionUpdateNotification: React.FC<VersionUpdateNotificationProps> = ({
    enabled = true,
    checkInterval = 5 * 60 * 1000, // 5 minutes
    position = 'top',
    className = '',
    onDismiss,
    onRefresh,
}) => {
    const { t } = useTranslation(['core']);
    const { hasUpdate } = useVersionChecker({
        enabled,
        checkInterval,
    });

    const [isDismissed, setIsDismissed] = React.useState(false);

    const handleDismiss = () => {
        setIsDismissed(true);
        if (onDismiss) {
            onDismiss();
        }
    };

    const handleRefresh = () => {
        if (onRefresh) {
            onRefresh();
        } else {
            // Default behavior: reload the page
            window.location.reload();
        }
    };

    if (!hasUpdate || isDismissed) {
        return null;
    }

    const notificationClass = `${styles.Notification} ${position === 'top' ? styles.Top : styles.Bottom} ${className}`;

    return (
        <div className={notificationClass} role="alert">
            <div className={styles.Content}>
                {/* Update icon */}
                <svg className={styles.Icon} viewBox="0 0 24 24">
                    <path d="M21 2v6h-6" />
                    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                    <path d="M3 22v-6h6" />
                    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                </svg>
                <div>
                    <div className={styles.Title}>{t('core:versionUpdate.title')}</div>
                    <div className={styles.Subtitle}>{t('core:versionUpdate.message')}</div>
                </div>
            </div>

            <div className={styles.Actions}>
                <button type="button" className={styles.RefreshButton} onClick={handleRefresh}>
                    {t('core:versionUpdate.updateButton')}
                </button>
                <button type="button" className={styles.DismissButton} onClick={handleDismiss}>
                    {t('core:versionUpdate.dismissButton')}
                </button>
            </div>
        </div>
    );
};

/**
 * Simple version banner for debugging/development
 */
export const VersionBanner: React.FC<{
    position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
    showCheckButton?: boolean;
}> = ({ position = 'bottom-right', showCheckButton = false }) => {
    const { t } = useTranslation(['core']);
    const { currentVersion, checkNow } = useVersionChecker({ enabled: false });

    const positionClass = {
        'top-left': styles.TopLeft,
        'top-right': styles.TopRight,
        'bottom-left': styles.BottomLeft,
        'bottom-right': styles.BottomRight,
    }[position];

    return (
        <div className={`${styles.Banner} ${positionClass}`}>
            <span>v{currentVersion}</span>
            {showCheckButton && (
                <button type="button" onClick={checkNow} className={styles.CheckButton}>
                    {t('core:versionUpdate.bannerCheckButton')}
                </button>
            )}
        </div>
    );
};
